/**
 * Dialog de sélection d'un article catalogue (extrait de OrDetail.jsx — iter31).
 * Recherche serveur avec debounce 200ms via GET /articles?q=…&actif_only=true.
 * iter165 — Ajoute en tête un choix "Article libre" (générique vide) qui permet
 * d'insérer une ligne prête à éditer sans article catalogue.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon, FileText, AlertCircle } from "lucide-react";
import api from "@/lib/api";

// iter165 — Article générique/libre placé toujours en tête du picker.
export const GENERIC_ARTICLE_SENTINEL = {
  __generic: true,
  id: "__generic__",
  libelle: "Article libre (sans catalogue)",
  reference: null,
  categorie: "libre",
  unite: "",
  prix_ht: 0,
  tva: 8.1,
};

export default function ArticlePickerDialog({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/articles", {
          params: { q, actif_only: true, limit: 50 },
        });
        setItems(data.items);
      } catch {
        setItems([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choisir un article du catalogue</DialogTitle>
        </DialogHeader>
        <div className="relative mb-3">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…"
            className="pl-10"
            autoFocus
          />
        </div>
        <div className="max-h-96 overflow-y-auto space-y-1">
          {/* iter165 — Article libre toujours en tête */}
          <button
            onClick={() => { onPick(GENERIC_ARTICLE_SENTINEL); onClose(); }}
            className="w-full text-left p-2 rounded border-2 border-dashed border-[#52a675]/40 bg-[#f0f9f4]/50 hover:border-[#52a675] hover:bg-[#f0f9f4]"
            data-testid="picker-article-generic"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#52a675]" />
              <div>
                <div className="text-sm font-semibold text-[#3d8a5c]">Article libre</div>
                <div className="text-xs text-gray-500">Créer une ligne vide à remplir manuellement</div>
              </div>
            </div>
          </button>

          {items.length === 0 && (
            <div className="text-sm text-gray-400 text-center py-6">
              Aucun article du catalogue
            </div>
          )}
          {items.map((a) => {
            const hasStock = a.stock_quantite !== null && a.stock_quantite !== undefined;
            const low = hasStock && a.stock_seuil_alerte != null && a.stock_seuil_alerte > 0 && a.stock_quantite <= a.stock_seuil_alerte;
            return (
            <button
              key={a.id}
              onClick={() => {
                onPick(a);
                onClose();
              }}
              className="w-full text-left p-2 rounded border border-gray-100 hover:border-[#52a675] hover:bg-[#f0f9f4]"
              data-testid={`picker-article-${a.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{a.libelle}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                    {a.reference && (
                      <code className="bg-gray-50 px-1 rounded">{a.reference}</code>
                    )}
                    <span>{a.categorie} · {a.unite}</span>
                    {hasStock && (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${low ? "bg-red-50 text-red-700 border border-red-200" : "bg-gray-50 text-gray-600 border border-gray-200"}`}>
                        {low && <AlertCircle className="w-3 h-3" />}
                        stock : {a.stock_quantite}{a.stock_unite ? ` ${a.stock_unite}` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="font-mono font-bold text-sm">
                  {a.prix_ht.toFixed(2)} CHF
                </div>
              </div>
            </button>
          );})}
        </div>
      </DialogContent>
    </Dialog>
  );
}
